import React, { useState, useMemo } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';
import { styled } from '@mui/material/styles';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

const KodBenaJournalImportModal = ({
    open,
    onClose,
    previewKodBenaJournal,
    commitKodBenaJournal,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    client,
    token,
    palCodes = [],
    palCodeCategoryOptions = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    user,
    setAccountCardOptions
}) => {
    const customStyles = {
        container: (provided) => ({ ...provided, width: '100%', boxSizing: 'border-box !important' }),
        control: (provided) => ({
            ...provided, minHeight: '40px', width: '100% !important', borderRadius: '8px',
            background: '#F5F5F6', border: 'none', boxShadow: 'none', padding: '0 10px',
            textAlign: 'left', boxSizing: 'border-box !important', border: '1px solid transparent',
            '&:focus-within': { border: '2px solid var(--Foundation-Blue-Normal, #304FFF)' },
        }),
        placeholder: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        singleValue: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        menu: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        option: (provided, state) => ({
            ...provided, backgroundColor: state.isSelected ? '#F9AA2A' : state.isFocused ? '#ebebeb' : null, color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided, color: state.isFocused ? 'darkgray' : 'black', '&:hover': { color: 'darkgray' },
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 2002 }),
    };

    const [mdbFile, setMdbFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [missingSortCodes, setMissingSortCodes] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [editableMissingSortCodes, setEditableMissingSortCodes] = useState([]);
    const [parsedEntries, setParsedEntries] = useState(0);

    const handleMdbFileDrop = files => {
        if (files?.length) setMdbFile(files[0]);
    };

    const handlePreview = async () => {
        if (!mdbFile) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = await previewKodBenaJournal(mdbFile);

            setMissingSortCodes(res?.missingSortCodes ?? []);
            setMissingAccountCards(res?.missingAccountCards ?? []);
            setParsedEntries(res?.parsedJournalEntries ?? 0);

            setEditableMissingSortCodes((res?.missingSortCodes ?? []).map(sc => ({
                sortCodeId: sc.sortCodeId,
                sortCodeName: sc.sortCodeName || '',
                sortCodeVatType: null,
                sortCodeGroup: null,
                participatesInProfitAndLoss: null,
                vat: sc.vat ?? null
            })));

            setPreviewDone(true);
        } catch (err) {
            console.error('KodBena preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!mdbFile) return;

        try {
            setProcessing(true);

            // Create missing sortcodes
            if (editableMissingSortCodes.length > 0) {
                await createMissingSortCodes(editableMissingSortCodes);
            }

            const res = await commitKodBenaJournal(mdbFile);

            const errorCount = res?.errorCount ?? res?.[3] ?? 0;
            if (errorCount === 0) {
                handleClose();
            }
        } catch (err) {
            console.error('KodBena import error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissingSortCode = (index, field, value) => {
        setEditableMissingSortCodes(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });
    };

    const missingIncomplete = useMemo(
        () => editableMissingSortCodes.some(sc =>
            !sc.sortCodeName || !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null || sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null || sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null || sc.vat === undefined
        ),
        [editableMissingSortCodes]
    );

    const handleClose = () => {
        setMdbFile(null);
        setPreviewDone(false);
        setMissingSortCodes([]);
        setMissingAccountCards([]);
        setEditableMissingSortCodes([]);
        setParsedEntries(0);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportKodBenaJournal') || 'KodBena - ייבוא פקודות יומן'}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div>
                        <h5>{t('UploadFile')} (.mdb)</h5>
                        <DropzoneArea
                            key={mdbFile ? mdbFile.name : 'kodbena-empty'}
                            onDrop={handleMdbFileDrop}
                            accept=".mdb"
                            filesLimit={1}
                        />
                        {mdbFile && <p>{t('FileSelected')}: {mdbFile.name}</p>}
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button variant="contained" onClick={handlePreview} disabled={!mdbFile || processing}>
                            {t('Preview')}
                        </Button>
                        <Button variant="contained" color="success"
                            disabled={!previewDone || processing || missingIncomplete || missingAccountCards.length > 0}
                            onClick={handleCreateAndCommit}>
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#666' }}>
                                {t('ParsedRows')}: <strong>{parsedEntries} {t('JournalEntries')}</strong>
                            </p>

                            {/* Missing SortCodes */}
                            {editableMissingSortCodes.length > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <h5>{t('MissingSortCodes')}</h5>
                                    <table className="sortcode-table editable">
                                        <thead>
                                            <tr>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissingSortCodes.map((sc, i) => (
                                                <tr key={sc.sortCodeId}>
                                                    <td><TextField fullWidth value={sc.sortCodeId} variant="standard" InputProps={{ readOnly: true, disableUnderline: true, sx: { height: '48px', padding: '0 10px', borderRadius: '8px', background: '#F5F5F6' } }} /></td>
                                                    <td><TextField fullWidth value={sc.sortCodeName} onChange={e => updateMissingSortCode(i, 'sortCodeName', e.target.value)} variant="standard" InputProps={{ disableUnderline: true, sx: { height: '48px', padding: '0 10px', borderRadius: '8px', background: !sc.sortCodeName ? 'white' : '#F5F5F6', border: !sc.sortCodeName ? '1.5px solid #f44336' : '1px solid transparent' } }} /></td>
                                                    <td><Select placeholder={t('SelectSortCodeVatType')} options={formattedSortCodeVatType} value={formattedSortCodeVatType.find(opt => opt.value === sc.sortCodeVatType?.toString()) || null} onChange={opt => updateMissingSortCode(i, 'sortCodeVatType', opt.value)} styles={{ ...customStyles, control: (provided) => ({ ...customStyles.control(provided), border: sc.sortCodeVatType !== null && sc.sortCodeVatType !== undefined ? '1px solid transparent' : '1.5px solid #f44336' }) }} /></td>
                                                    <td><Select placeholder={t('SelectSortCodeGroupName')} options={formattedSortCodesGroupNamesOptions} value={formattedSortCodesGroupNamesOptions.find(opt => opt.value === sc.sortCodeGroup) || null} onChange={opt => updateMissingSortCode(i, 'sortCodeGroup', opt.value)} styles={customStyles} /></td>
                                                    <td><Select placeholder={t('participatesInProfitAndLoss')} options={[{ label: t('Yes'), value: true }, { label: t('No'), value: false }]} value={sc.participatesInProfitAndLoss === true ? { label: t('Yes'), value: true } : sc.participatesInProfitAndLoss === false ? { label: t('No'), value: false } : null} onChange={opt => updateMissingSortCode(i, 'participatesInProfitAndLoss', opt.value)} styles={{ ...customStyles, control: provided => ({ ...customStyles.control(provided), border: sc.participatesInProfitAndLoss !== null && sc.participatesInProfitAndLoss !== undefined ? '1px solid transparent' : '1.5px solid #f44336' }) }} /></td>
                                                    <td><Select placeholder={t('VAT')} options={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }))} value={Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v })).find(opt => opt.value === sc.vat) || null} onChange={opt => updateMissingSortCode(i, 'vat', opt.value)} styles={{ ...customStyles, control: provided => ({ ...customStyles.control(provided), border: sc.vat !== null && sc.vat !== undefined ? '1px solid transparent' : '1.5px solid #f44336' }) }} isSearchable={false} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Missing Account Cards */}
                            {missingAccountCards.length > 0 && (
                                <div style={{ marginTop: 25 }}>
                                    <MdbMissingAccountCardsTable
                                        missingAccountCards={missingAccountCards.map(card => ({ 
                                            cardId: typeof card === 'string' ? card : card.cardId || card.CardId || '', 
                                            cardName: typeof card === 'string' ? '' : card.cardName || card.CardName || '' 
                                        }))}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => { if (mdbFile) await handlePreview(); }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KodBenaJournalImportModal;

